import { useFetcher, useSearchParams, data } from 'react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { optionalProtectedResourceAction, optionalProtectedResourceLoader } from '~/shell/utils/authUtil.server';
import { FORM_FIELDS } from '~/common/constants/formfields';
import { getCcLocations } from '~/shell/location.server';
import { requireFormString } from '~/common/utils/formDataUtil';
import { getSelectedLocation, setSelectedLocation } from '~/shell/utils/locationUtil.server';
import { PgArea } from '~/common/types/area';
import { useQueryClient } from '@tanstack/react-query';

export const loader = optionalProtectedResourceLoader(async ({ request, api }) => {
  const locations = await getCcLocations(api);

  const ccId = await getSelectedLocation(request);

  return {
    locations: locations,
    ccId: ccId
  };
});

export const action = optionalProtectedResourceAction(async ({ request }) => {
  const headers = new Headers();

  const formData = await request.formData();
  const ccId = requireFormString(formData, FORM_FIELDS.CcId);

  const ccValue = Number.parseInt(ccId, 10);
  await setSelectedLocation(ccValue, headers);

  return data(
    {
      ccId: ccValue
    },
    { headers }
  );
});

export function useLocations() {
  const { load, data, state } = useFetcher<typeof loader>();
  const fetcher = useFetcher();
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  useEffect(() => {
    load(`/${i18n.language}/${PgArea}/resources/cc-locations/location`);
  }, [i18n.language, load, searchParams]);

  const setCcId = (id: number) => {
    queryClient.clear();
    const formData = new FormData();
    formData.append(FORM_FIELDS.CcId, id.toString());
    fetcher.submit(formData, {
      method: 'post',
      action: `/${i18n.language}/${PgArea}/resources/cc-locations/location`
    });
  };

  return {
    locations: data?.locations ?? [],
    isLoading: state === 'loading',
    ccId: data?.ccId,
    setCcId
  };
}
